import Fuse from 'fuse.js';

import {
  ResultKind,
  getResultKindDisplayLabels,
  SmartSearchSelectOption,
  FUSE_SEARCH_KEYS,
} from '../resultKindConfig';
import { getElectrolyteBaseUrl } from '@/constants/urls';
import { EmployeeDirectoryListing } from '@/types/employees';
import { getDisplayFirstName } from 'common/utils/getFullName';

// Transform employees into searchable format
const transformEmployeesToSearchable = (
  employees: EmployeeDirectoryListing[]
): SmartSearchSelectOption[] => {
  const electrolyteBaseUrl = getElectrolyteBaseUrl();

  return employees.map(employee => {
    const result: SmartSearchSelectOption = {
      kind: ResultKind.EMPLOYEE,
      url: `${electrolyteBaseUrl}/employees/${employee.id}`,
      firstName: getDisplayFirstName(employee) ?? employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      jobTitle: employee.jobTitle,
      jobTitleFrCa: employee.jobTitleFrCa,
    };
    return result;
  });
};

export const search = async (
  staticSearchableData: SmartSearchSelectOption[],
  searchTerm: string,
  isAdmin: boolean,
  employeesFetcher?: (term: string) => Promise<EmployeeDirectoryListing[]>
) => {
  return await Promise.resolve().then(async () => {
    // Trim search term once for consistency
    const trimmedSearchTerm = searchTerm.trim();

    // For empty searches, return empty results to avoid confusion
    if (!trimmedSearchTerm) {
      return [];
    }

    // Search static data (pages, actions) with trimmed term
    const staticFuse = new Fuse<SmartSearchSelectOption>(staticSearchableData, {
      keys: FUSE_SEARCH_KEYS,
      threshold: 0.5,
    });

    const staticResults = staticFuse.search(trimmedSearchTerm);

    // Fetch and search employees only if user is admin and has search term
    let employeeResults: SmartSearchSelectOption[] = [];
    if (isAdmin && employeesFetcher) {
      try {
        const employees = await employeesFetcher(trimmedSearchTerm);
        employeeResults = transformEmployeesToSearchable(employees);
      } catch (error) {
        console.error('Error fetching employees for search:', error);
      }
    }

    // Combine all results
    const allResults = [
      ...employeeResults,
      ...staticResults.map(result => result.item),
    ];

    // Store the search results in a map with the result kind as the key
    const resultMap: { [key in ResultKind]?: SmartSearchSelectOption[] } = {};

    allResults.forEach(resultItem => {
      const resultItemKind = resultItem.kind;

      // Get the existing array of result items of the same kind or initialize an empty array
      const searchResultsOfKind = resultMap[resultItemKind] ?? [];

      if (searchResultsOfKind !== undefined) {
        // Push the result item to the corresponding array in the map
        searchResultsOfKind.push(resultItem);
        // Update the map with the new array of result items
        resultMap[resultItemKind] = searchResultsOfKind;
      } else {
        console.error(`cannot render unknown result kind: ${resultItemKind}`);
      }
    });

    // Map each ResultKind in the resultMap to its display label
    return Object.entries(resultMap).map(([kind, options]) => ({
      label: getResultKindDisplayLabels()[kind as ResultKind],
      options,
    }));
  });
};
